import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslate } from '../../../mixins/translate/translate';
import { openApiFetchDelete, openApiFetchGet, openApiFetchPost } from '../../../store/openApiFetch';
import { EditPanel, EditPanelButtons, EditPanelSection, PanelBody, PanelButton } from '../../elements';
import { Spinner } from '../../spinners';

interface ISurveyEditVideoStreamProps {
    surveyId: string;
}

function RetryingVideo(props: { sourceUrl: string }) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const checkInterval = useRef<NodeJS.Timeout | null>(null);

    const loadVideo = () => {
        const video = videoRef.current;
        if (video && video.networkState === video.NETWORK_NO_SOURCE) {
            // NETWORK_NO_SOURCE: no information whether video is available.
            video.load();
        } else if (video && video.networkState === video.NETWORK_LOADING) {
            // NETWORK_LOADING: browser is downloading data.
            if (checkInterval.current) {
                clearInterval(checkInterval.current);
            }
        } else if (video) {
            try {
                void video.play();
            } catch {}
        }
    };

    useEffect(() => {
        checkInterval.current = setInterval(loadVideo, 5000); // Retry every 5 seconds.

        return () => {
            if (checkInterval.current) {
                clearInterval(checkInterval.current); // Clean up interval on unmount.
            }
        };
    }, []);

    return (
        <video ref={videoRef} crossOrigin="anonymous" style={{ width: '100%' }} autoPlay controls>
            <source src={props.sourceUrl} type="video/mp4" />
        </video>
    );
}
export function SurveyEditVideoStream(props: ISurveyEditVideoStreamProps) {
    const { surveyId } = props;
    const translate = useTranslate();
    const [state, setState] = React.useState<{ isLoading: boolean; rtmpUrl: string | null; hasServerError: boolean; videoUrl: string | null }>({
        hasServerError: false,
        isLoading: true,
        rtmpUrl: null,
        videoUrl: null,
    });

    const load = useCallback(async () => {
        try {
            const response = await openApiFetchGet('/api/surveys/{surveyId}/videoStream', { surveyId });
            setState({ isLoading: false, hasServerError: false, rtmpUrl: response.rtmpUrl, videoUrl: response.videoUrl });
        } catch (error) {
            if ('statusCode' in error && error.statusCode === 404) {
                setState({ isLoading: false, hasServerError: false, rtmpUrl: null, videoUrl: null });
            } else {
                setState({ isLoading: false, hasServerError: true, rtmpUrl: null, videoUrl: null });
            }
        }
    }, [surveyId]);

    useEffect(() => {
        void load();
    }, [load]);

    async function createStream() {
        setState({ isLoading: true, hasServerError: false, rtmpUrl: null, videoUrl: null });
        const response = await openApiFetchPost('/api/surveys/{surveyId}/videoStream', { surveyId }, {});
        setState({ isLoading: false, hasServerError: false, rtmpUrl: response.rtmpUrl, videoUrl: response.videoUrl });
    }

    async function deleteStream() {
        setState({ isLoading: true, hasServerError: false, rtmpUrl: null, videoUrl: null });
        await openApiFetchDelete('/api/surveys/{surveyId}/videoStream', { surveyId });
        setState({ isLoading: false, hasServerError: false, rtmpUrl: null, videoUrl: null });
    }

    return (
        <EditPanel bottomMargin={true} noPadding={true}>
            <PanelBody isForm={true}>
                {state.isLoading && <Spinner />}

                {!state.isLoading && state.rtmpUrl && state.videoUrl && (
                    <>
                        <EditPanelSection>
                            RTMP URL: <code>{state.rtmpUrl}</code>
                        </EditPanelSection>
                        <EditPanelSection>
                            <RetryingVideo sourceUrl={state.videoUrl} />
                        </EditPanelSection>
                        <EditPanelButtons>
                            <PanelButton onClick={deleteStream} primary text={translate('common.removeItem', translate('videoStream.videoStream'))} />
                        </EditPanelButtons>
                    </>
                )}

                {!state.isLoading && !state.hasServerError && state.rtmpUrl === null && (
                    <EditPanelButtons>
                        <p className="text-center mb30">This survey does not have a video stream available.</p>
                        <PanelButton onClick={createStream} primary text={translate('common.addItem', translate('videoStream.videoStream'))} />
                    </EditPanelButtons>
                )}

                {!state.isLoading && state.hasServerError && (
                    <EditPanelSection>
                        <p className="text-center mb30">
                            There was an issue with communicating with the streaming server. Please verify all server settings.
                        </p>
                    </EditPanelSection>
                )}
            </PanelBody>
        </EditPanel>
    );
}
